// src/components/modals/FaunaModal.jsx
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * FaunaModal
 * Props:
 * - fauna: { name, imageUrl, jep, blogPost, description? }
 * - theme: 'light' | 'dark'
 * - onClose: () => void
 */
export default function FaunaModal({ fauna, theme = 'light', onClose }) {
  const isOpen = !!fauna;

  // refs/hooks must be declared unconditionally
  const closeRef = useRef(null);

  // Close on ESC + lock body scroll when open
  useEffect(() => {
    if (!isOpen) return;

    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);

    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, onClose]);

  // Autofocus the close button for quick keyboard access
  useEffect(() => {
    if (!isOpen) return;
    closeRef.current?.focus();
  }, [isOpen]);

  if (!isOpen) return null;

  const isLight = theme === 'light';
  const bgClass = isLight ? 'bg-white' : 'bg-gray-800';
  const textClass = isLight ? 'text-gray-900' : 'text-gray-100';
  const overlayClass = 'bg-black bg-opacity-60';

  const titleId = 'fauna-modal-title';

  const overlay = (
    <div
      className={`fixed inset-0 flex items-center justify-center ${overlayClass}`}
      style={{ zIndex: 10000 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={onClose} // click outside closes
    >
      <div
        className={`relative p-6 rounded-lg max-w-sm w-full shadow-2xl ${bgClass}`}
        onClick={(e) => e.stopPropagation()} // don’t close when clicking inside
      >
        <button
          ref={closeRef}
          className={`absolute top-2 right-2 ${isLight ? 'text-gray-600 hover:text-black' : 'text-gray-300 hover:text-white'} focus:outline-none`}
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </button>

        {fauna?.imageUrl && (
          <img
            src={fauna.imageUrl}
            alt={fauna.name || 'Fauna image'}
            className="w-full h-auto mb-4 rounded"
          />
        )}

        <h3 id={titleId} className={`text-xl font-bold mb-2 ${textClass}`}>
          {fauna?.name ?? 'Fauna'}
        </h3>

        {fauna?.description && (
          <div className={`text-lg mb-3 ${textClass}`}>{fauna.description}</div>
        )}

        <div className="flex flex-wrap gap-3">
          {fauna?.jep && (
            <a
              href={`https://openjdk.org/jeps/${fauna.jep}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              JEP {fauna.jep}
            </a>
          )}
          {fauna?.blogPost && (
            <a
              href={fauna.blogPost}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Read Blog Post
            </a>
          )}
        </div>
      </div>
    </div>
  );

  // Render above all zone layers
  return createPortal(overlay, document.body);
}
